import { s } from "../style.js";
import type { Page, Task } from "../types";

const metricLabel = s(
  "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px"
);
const metricNum = s(
  "font-family:var(--font-heading); font-weight:800; font-size:76px; line-height:0.9; letter-spacing:-0.04em"
);
const sectionHead = s(
  "flex:none; display:flex; align-items:center; gap:12px; padding:14px 26px; border-bottom:2px solid var(--color-divider); font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
);

export default function HomePage({
  platforms,
  tasks,
  prs,
  deploys,
  tasksLoading,
  pulseAnim,
  onNavigate,
  onOpenTask,
}: { platforms: any[]; tasks: Task[]; prs: any[]; deploys: any[]; tasksLoading: boolean; pulseAnim: string; onNavigate: (page: Page) => void; onOpenTask: (task: Task) => void }) {
  const up = platforms.filter((p) => p.healthKey === "up").length;
  const total = platforms.length;
  const onHold = tasksLoading ? [] : tasks.filter((t) => t.status === "On hold");
  const inProgress = tasksLoading ? [] : tasks.filter((t) => t.status === "In progress");
  const stalePrs = prs.filter((p) => p.stale);
  const incTotal = platforms.reduce((a, p) => a + p.incidents, 0);

  const attention = [
    ...platforms
      .filter((p) => p.healthKey !== "up")
      .map((p) => ({
        key: "p-" + p.name,
        kind: p.label,
        label: p.name,
        sub: p.region + " · " + p.version,
        to: "server",
      })),
    ...onHold.map((t) => ({
      key: "t-" + t.id,
      kind: "On hold",
      label: "ATL-" + t.id,
      sub: t.title,
      to: "sprint",
    })),
    ...stalePrs.map((p) => ({
      key: "r-" + p.title,
      kind: "Stale",
      label: p.title,
      sub: p.repo + " · " + p.wait,
      to: "review",
    })),
  ];

  return (
    <div style={s("flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden")}>
      <div
        style={s(
          "flex:none; display:grid; grid-template-columns:repeat(4,1fr); border-bottom:2px solid var(--color-divider)"
        )}
      >
        <div style={s("padding:20px 32px; border-right:2px solid var(--color-divider)")}>
          <div style={metricLabel}>Platforms operational</div>
          <div style={s("display:flex; align-items:baseline; gap:18px")}>
            <div style={metricNum}>
              {up}
              <span style={s("font-size:36px; color:var(--color-neutral-600)")}>/{total}</span>
            </div>
            {up === total ? (
              <span
                style={s(
                  "display:inline-flex; align-items:center; gap:8px; padding:6px 14px; background:var(--color-text); color:var(--color-bg); font-size:14px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
                )}
              >
                <span style={s("width:10px; height:10px; background:var(--color-bg)")} />
                All clear
              </span>
            ) : (
              <span
                style={s(
                  "display:inline-flex; align-items:center; gap:8px; padding:6px 14px; background:var(--color-accent); color:var(--on-accent); font-size:14px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
                )}
              >
                <span style={{ ...s("width:10px; height:10px; background:var(--on-accent)"), animation: pulseAnim }} />
                Attention
              </span>
            )}
          </div>
        </div>
        <div
          style={s(
            "padding:20px 32px; border-right:2px solid var(--color-divider); background:var(--color-accent); color:var(--on-accent)"
          )}
        >
          <div style={{ ...metricLabel, color: undefined, opacity: 0.85 }}>On hold</div>
          <div style={metricNum}>{onHold.length}</div>
        </div>
        <div style={s("padding:20px 32px; border-right:2px solid var(--color-divider)")}>
          <div style={metricLabel}>Open PRs</div>
          <div style={metricNum}>
            {prs.length}
            {stalePrs.length > 0 && (
              <span style={s("font-size:30px; color:var(--color-accent)")}> · {stalePrs.length} stale</span>
            )}
          </div>
        </div>
        <div style={s("padding:20px 32px")}>
          <div style={metricLabel}>Open incidents</div>
          <div style={{ ...metricNum, color: incTotal > 0 ? "var(--color-accent)" : undefined }}>
            {incTotal}
          </div>
        </div>
      </div>

      <div
        style={s(
          "flex:1; display:grid; grid-template-columns:1fr 1fr 420px; min-height:0; overflow:hidden"
        )}
      >
        <section
          style={s(
            "min-width:0; min-height:0; display:flex; flex-direction:column; overflow:hidden; border-right:2px solid var(--color-divider)"
          )}
        >
          <div style={sectionHead}>
            Needs attention
            <span
              style={s(
                "display:inline-flex; align-items:center; justify-content:center; min-width:26px; height:26px; padding:0 6px; background:var(--color-accent); color:var(--on-accent); font-size:14px; font-variant-numeric:tabular-nums"
              )}
            >
              {attention.length}
            </span>
          </div>
          <div style={s("flex:1; min-height:0; overflow:auto")}>
            {attention.length === 0 ? (
              <div
                style={s(
                  "padding:40px 26px; font-size:18px; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase"
                )}
              >
                All clear
              </div>
            ) : (
              attention.map((a) => (
                <div
                  key={a.key}
                   onClick={() => onNavigate(a.to as Page)}
                  style={s(
                    "display:flex; align-items:center; gap:14px; padding:16px 26px; border-bottom:1px solid rgba(243,242,242,0.4); background:var(--color-accent); color:var(--on-accent); cursor:pointer"
                  )}
                >
                  <span
                    style={{
                      ...s("width:12px; height:12px; flex:none; background:var(--on-accent)"),
                      animation: pulseAnim,
                    }}
                  />
                  <div style={s("flex:1; min-width:0")}>
                    <div
                      style={s(
                        "font-size:20px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; text-wrap:pretty"
                      )}
                    >
                      {a.label}
                    </div>
                    <div
                      style={s(
                        "font-size:14px; opacity:0.85; margin-top:2px; font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase"
                      )}
                    >
                      {a.kind} · {a.sub}
                    </div>
                  </div>
                  <span style={s("font-size:22px; line-height:1")}>›</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section
          style={s(
            "min-width:0; min-height:0; display:flex; flex-direction:column; overflow:hidden; border-right:2px solid var(--color-divider)"
          )}
        >
          <div style={sectionHead}>Platforms</div>
          <div style={s("flex:1; min-height:0; overflow:auto")}>
            {platforms.map((p) => {
              const unhealthy = p.healthKey !== "up";
              return (
                <div
                  key={p.name}
                  onClick={() => onNavigate("server")}
                  style={s(
                    "display:flex; align-items:center; gap:14px; padding:16px 26px; border-bottom:1px solid var(--color-divider); cursor:pointer"
                  )}
                >
                  <span
                    style={{
                      ...s("width:12px; height:12px; flex:none; background:" + p.dot),
                      animation: unhealthy ? pulseAnim : "none",
                    }}
                  />
                  <div style={s("flex:1; min-width:0")}>
                    <div
                      style={s(
                        "font-size:20px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em"
                      )}
                    >
                      {p.name}
                    </div>
                    <div
                      style={s(
                        "font-size:13px; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase; margin-top:2px"
                      )}
                    >
                      {p.region} · {p.version}
                    </div>
                  </div>
                  <div style={s("text-align:right")}>
                    <div
                      style={s(
                        "font-size:18px; font-family:var(--font-heading); font-weight:800; font-variant-numeric:tabular-nums"
                      )}
                    >
                      {p.p95Latency != null ? `${p.p95Latency}ms` : "—"}
                    </div>
                    <div
                      style={{
                        ...s(
                          "font-size:12px; font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase"
                        ),
                        color: p.incidents > 0 ? "var(--color-accent)" : "var(--color-neutral-600)",
                      }}
                    >
                      {p.incidents} incident{p.incidents === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span style={s("font-size:22px; color:var(--color-neutral-500); line-height:1")}>›</span>
                </div>
              );
            })}
          </div>
        </section>

        <aside
          style={s(
            "min-width:0; min-height:0; background:var(--color-text); color:var(--color-bg); display:flex; flex-direction:column; overflow:auto"
          )}
        >
          <div
            style={s(
              "flex:none; display:flex; align-items:center; gap:12px; padding:16px 26px; border-bottom:2px solid rgba(243,242,242,0.45); font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
            )}
          >
            {inProgress.length} In progress
          </div>
          {inProgress.map((t) => (
            <div
              key={t.id}
              onClick={() => onOpenTask(t)}
              style={s(
                "padding:20px 26px; border-bottom:2px solid rgba(243,242,242,0.45); cursor:pointer"
              )}
            >
              <div style={s("display:flex; align-items:center; gap:8px; margin-bottom:8px")}>
                <span
                  style={s(
                    "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; opacity:0.8"
                  )}
                >
                  ATL-{t.id}
                </span>
                {t.repos.slice(0, 2).map((repo, i) => (
                  <span
                    key={repo}
                    style={s(
                      "display:inline-flex; align-items:center; padding:2px 9px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-bg); color:var(--color-text)"
                    )}
                  >
                    {t.repoNames?.[i] || repo}
                  </span>
                ))}
              </div>
              <div
                style={s(
                  "font-size:24px; line-height:1.06; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.02em; text-wrap:pretty"
                )}
              >
                {t.title}
              </div>
              <div style={s("display:flex; align-items:center; gap:10px; margin-top:12px")}>
                <span
                  style={s(
                    "width:32px; height:32px; background:var(--color-bg); color:var(--color-text); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:13px"
                  )}
                >
                  {t.initials || "?"}
                </span>
                <span style={s("font-size:15px")}>{t.owner || "Unassigned"}</span>
              </div>
            </div>
          ))}

          <div
            style={s(
              "flex:none; display:flex; align-items:center; gap:12px; padding:16px 26px; border-bottom:2px solid rgba(243,242,242,0.45); font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
            )}
          >
            Recent deploys
          </div>
          {deploys.map((d) => (
            <div
              key={d.name}
              style={s(
                "display:flex; align-items:center; gap:12px; padding:14px 26px; border-bottom:2px solid rgba(243,242,242,0.45)"
              )}
            >
              <span
                style={s(
                  "font-size:16px; font-family:var(--font-heading); font-weight:800; font-variant-numeric:tabular-nums; opacity:0.8"
                )}
              >
                {d.time}
              </span>
              <span
                style={s(
                  "flex:1; min-width:0; font-size:17px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis"
                )}
              >
                {d.name}
              </span>
              <span
                style={{
                  ...s(
                    "font-size:14px; font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase"
                  ),
                  color: d.failed ? "var(--color-accent)" : undefined,
                  opacity: d.failed ? undefined : 0.7,
                }}
              >
                {d.failed ? "failed" : d.duration}
              </span>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
